import JSZip from 'jszip';

// ═══════════════════════════════════════════════════════════════
// Čtení sledovaných změn z OOXML (Fáze 3).
//
// Mammoth Track Changes nezvládá:
//   • vložený text (<w:ins>) propustí, ale ZTRATÍ informaci, že je vložený
//   • smazaný text (<w:del>) ZAHODÍ úplně
// Proto čteme word/document.xml sami.
//
// KLÍČOVÉ pro umístění: u každé změny si bereme i KONTEXT — text, který jí
// v odstavci předchází. Podle něj pak import najde přesné místo v HTML.
// Bez kontextu by se smazaný text (který mammoth zahodil, takže v HTML není
// vůbec) musel umísťovat odhadem — a to nefunguje.
// ═══════════════════════════════════════════════════════════════

export interface TrackedRun {
  kind: 'ins' | 'del';
  text: string;
  author: string;
  date: string;
  /** Pořadí výskytu v dokumentu. */
  order: number;
  /**
   * Text, který změně bezprostředně předchází v témže odstavci (bez
   * smazaných úseků — tak, jak ho vidí mammoth v HTML). Kotva pro umístění.
   */
  before: string;
  /** Text, který za změnou následuje v témže odstavci. */
  after: string;
}

export interface TrackChangesResult {
  runs: TrackedRun[];
}

export async function extractTrackChanges(buffer: ArrayBuffer): Promise<TrackChangesResult> {
  const empty: TrackChangesResult = { runs: [] };
  let xml: string;
  try {
    const zip = await JSZip.loadAsync(buffer);
    const doc = zip.file('word/document.xml');
    if (!doc) return empty;
    xml = await doc.async('string');
  } catch {
    return empty;
  }

  const runs: TrackedRun[] = [];
  let order = 0;

  // Zpracováváme po odstavcích — kontext má smysl jen v rámci odstavce.
  const paraRe = /<w:p\b[\s\S]*?<\/w:p>/g;
  let pm: RegExpExecArray | null;
  while ((pm = paraRe.exec(xml)) !== null) {
    order = parseParagraph(pm[0], runs, order);
  }

  return { runs };
}

/**
 * Projde jeden odstavec a posbírá z něj změny i s kontextem.
 * Vrací nové pořadové číslo.
 */
function parseParagraph(para: string, out: TrackedRun[], order: number): number {
  // Posbírej segmenty odstavce v pořadí: běžný text / ins / del.
  interface Seg { kind: 'text' | 'ins' | 'del'; text: string; author?: string; date?: string }
  const segs: Seg[] = [];

  // Rozdělíme odstavec na části podle <w:ins>/<w:del>; mezi nimi je běžný text.
  const re = /<w:(ins|del)\b([^>]*)>([\s\S]*?)<\/w:\1>/g;
  let last = 0;
  let m: RegExpExecArray | null;

  while ((m = re.exec(para)) !== null) {
    // Běžný text před touto změnou.
    const plain = extractPlainText(para.slice(last, m.index));
    if (plain) segs.push({ kind: 'text', text: plain });

    const kind = m[1] as 'ins' | 'del';
    const attrs = m[2];
    const body = m[3];
    const text = kind === 'del' ? extractDelText(body) : extractPlainText(body);
    if (text) {
      segs.push({
        kind,
        text,
        author: attr(attrs, 'w:author') || 'Neznámý autor',
        date: normalizeDate(attr(attrs, 'w:date')),
      });
    }
    last = m.index + m[0].length;
  }
  // Zbytek odstavce za poslední změnou.
  const tail = extractPlainText(para.slice(last));
  if (tail) segs.push({ kind: 'text', text: tail });

  // Z segmentů vyrob změny s kontextem.
  // `before` = text, který v HTML předchází (tj. běžný text + vložené úseky,
  // protože ty mammoth do HTML propustí; smazané ne).
  for (let i = 0; i < segs.length; i++) {
    const s = segs[i];
    if (s.kind === 'text') continue;

    let before = '';
    for (let j = 0; j < i; j++) {
      if (segs[j].kind !== 'del') before += segs[j].text;
    }
    let after = '';
    for (let j = i + 1; j < segs.length; j++) {
      if (segs[j].kind !== 'del') after += segs[j].text;
    }

    out.push({
      kind: s.kind,
      text: s.text,
      author: s.author!,
      date: s.date!,
      order: order++,
      before,
      after,
    });
  }

  return order;
}

/** Text z <w:t> (běžný a vložený text). */
function extractPlainText(xml: string): string {
  const parts: string[] = [];
  const re = /<w:t\b[^>]*>([\s\S]*?)<\/w:t>/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(xml)) !== null) parts.push(unescapeXml(m[1]));
  return parts.join('');
}

/** Text z <w:delText> (smazaný text — mammoth ho zahodí). */
function extractDelText(xml: string): string {
  const parts: string[] = [];
  const re = /<w:delText\b[^>]*>([\s\S]*?)<\/w:delText>/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(xml)) !== null) parts.push(unescapeXml(m[1]));
  return parts.join('');
}

function attr(attrs: string, name: string): string | null {
  const m = attrs.match(new RegExp(`\\b${name.replace(':', '\\:')}="([^"]*)"`));
  return m ? m[1] : null;
}

function normalizeDate(raw: string | null): string {
  if (!raw) return new Date().toISOString();
  const d = new Date(raw);
  return isNaN(d.getTime()) ? new Date().toISOString() : d.toISOString();
}

function unescapeXml(s: string): string {
  return s
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&amp;/g, '&');
}
